import React, { useState, useEffect, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config/api';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Fade
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  VpnKey as VpnKeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

// Tab Panel Component
const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`profile-tabpanel-${index}`}
    aria-labelledby={`profile-tab-${index}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

const Profile = memo(({ onBackToDashboard }) => {
  const { t } = useTranslation();
  // Add CSS animations for shimmer and pulse effects
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% {
          background-position: -200% 0;
        }
        100% {
          background-position: 200% 0;
        }
      }
      @keyframes pulse {
        0% {
          opacity: 1;
          transform: scale(1);
        }
        50% {
          opacity: 0.7;
          transform: scale(1.1);
        }
        100% {
          opacity: 1;
          transform: scale(1);
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [tabValue, setTabValue] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Profile data state
  const [profileData, setProfileData] = useState({
    id: null, // Will be set from localStorage
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    position: '',
    avatar: null,
    xhsProfile: '',
    tiktokProfile: ''
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Validation state
  const [errors, setErrors] = useState({});

  const loadProfileData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Get user data from localStorage
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        setProfileData(prev => ({
          ...prev,
          id: userData.id,
          firstName: userData.first_name || '',
          lastName: userData.last_name || '',
          email: userData.email || '',
          username: userData.username || '',
          position: userData.position || '',
          avatar: userData.avatar_url || null,
          xhsProfile: userData.xhs_profile || '',
          tiktokProfile: userData.tiktok_profile || '',
          createdAt: userData.created_at || null,
          lastLogin: userData.last_login || null
        }));
      } else {
        // Fallback: try to fetch from API with default ID
        const response = await fetch(API_ENDPOINTS.PROFILE(1));
        if (response.ok) {
          const userData = await response.json();
          setProfileData(prev => ({
            ...prev,
            id: userData.id,
            firstName: userData.first_name || '',
            lastName: userData.last_name || '',
            email: userData.email || '',
            username: userData.username || '',
            position: userData.position || '',
            avatar: userData.avatar_url || null,
            xhsProfile: userData.xhs_profile || '',
            tiktokProfile: userData.tiktok_profile || '',
            createdAt: userData.created_at || null,
            lastLogin: userData.last_login || null
          }));
        } else {
          throw new Error('Failed to load profile data');
        }
      }
    } catch (error) {
      console.error('Error loading profile data:', error);
      setSnackbar({
        open: true,
        message: t('profile.errorLoadingProfile'),
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // Load profile data from API/localStorage
    loadProfileData();
  }, [loadProfileData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };



  const validateForm = () => {
    const newErrors = {};
    
    if (!profileData.firstName.trim()) {
      newErrors.firstName = t('profile.firstNameRequired');
    }
    if (!profileData.lastName.trim()) {
      newErrors.lastName = t('profile.lastNameRequired');
    }
    if (!profileData.email.trim()) {
      newErrors.email = t('profile.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(profileData.email)) {
      newErrors.email = t('profile.emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.UPDATE_PROFILE(profileData.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          email: profileData.email,
          position: profileData.position,
          avatarUrl: profileData.avatar,
          xhsProfile: profileData.xhsProfile,
          tiktokProfile: profileData.tiktokProfile
        })
      });

      if (response.ok) {
        const result = await response.json();
        setProfileData(prev => ({
          ...prev,
          firstName: result.user.first_name,
          lastName: result.user.last_name,
          email: result.user.email,
          position: result.user.position,
          avatar: result.user.avatar_url,
          xhsProfile: result.user.xhs_profile,
          tiktokProfile: result.user.tiktok_profile
        }));
        
        setIsEditing(false);
        setSnackbar({
          open: true,
          message: t('profile.profileUpdated'),
          severity: 'success'
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: error.message || t('profile.errorUpdatingProfile'),
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset to original data
    loadProfileData();
    setErrors({});
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbar({
        open: true,
        message: t('profile.passwordsDoNotMatch'),
        severity: 'error'
      });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setSnackbar({
        open: true,
        message: t('profile.passwordMinLength'),
        severity: 'error'
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.UPDATE_PASSWORD(profileData.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      if (response.ok) {
        setChangePasswordDialog(false);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setSnackbar({
          open: true,
          message: t('profile.passwordChanged'),
          severity: 'success'
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setSnackbar({
        open: true,
        message: error.message || t('profile.errorChangingPassword'),
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <Box className="profile-container" sx={{ 
      width: '100%', 
      maxWidth: '100%', 
      mx: 'auto',
      px: { xs: 1, sm: 2, md: 3, lg: 3, xl: 4 }
    }}>
      <Fade in timeout={600}>
        <Box className="profile-content">
          {/* Header Section */}
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, sm: 3, md: 4 }, 
              mb: { xs: 2, sm: 3, md: 4 },
              mt: { xs: 2, sm: 3 },
              background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
              color: 'white',
              borderRadius: { xs: 2, sm: 3 },
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100%',
                height: '100%',
                backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(52, 152, 219, 0.1) 0%, transparent 50%)',
                pointerEvents: 'none'
              }}
            />
            
            <Box display="flex" alignItems="center" justifyContent="space-between" position="relative" zIndex={1} sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 3, sm: 0 } }}>
              <Box 
                className="profile-header-avatar-container"
                display="flex" 
                alignItems="center" 
                gap={{ xs: 2, sm: 3 }} 
                sx={{ 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  textAlign: { xs: 'center', sm: 'left' },
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  width: { xs: '100%', sm: 'auto' }
                }}
              >

                <Avatar
                  sx={{ 
                    width: { xs: 60, sm: 80, md: 100 }, 
                    height: { xs: 60, sm: 80, md: 100 }, 
                    bgcolor: '#3498db',
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                    border: '4px solid rgba(255,255,255,0.2)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    flexShrink: 0,
                    alignSelf: { xs: 'center', sm: 'flex-start' }
                  }}
                >
                  {profileData.avatar ? (
                    <img src={profileData.avatar} alt="Profile" />
                  ) : (
                    <PersonIcon />
                  )}
                </Avatar>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: { xs: 'center', sm: 'flex-start' },
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 700, 
                    mb: 1, 
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                  }}>
                    {profileData.username ? profileData.username.charAt(0).toUpperCase() + profileData.username.slice(1) : `${profileData.firstName} ${profileData.lastName}`}
                  </Typography>
                  <Typography variant="h6" sx={{ 
                    mb: 0, 
                    opacity: 0.9, 
                    fontWeight: 400,
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}>
                    {profileData.position}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: { xs: 2, sm: 0 } }}>
                {!isEditing ? (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                    sx={{ 
                      minWidth: { xs: '100%', sm: 140 },
                      height: { xs: 40, sm: 44, md: 48 },
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      fontWeight: 600,
                      background: 'linear-gradient(45deg, #3498db, #2980b9)',
                      boxShadow: '0 4px 20px rgba(52, 152, 219, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #2980b9, #1f5f8b)',
                        boxShadow: '0 6px 25px rgba(52, 152, 219, 0.5)'
                      }
                    }}
                  >
                    {t('profile.editProfile')}
                  </Button>
                ) : (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      disabled={isLoading}
                      sx={{ 
                        minWidth: { xs: '100%', sm: 140 },
                        height: { xs: 40, sm: 44, md: 48 },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        background: 'linear-gradient(45deg, #27ae60, #1e8449)',
                        boxShadow: '0 4px 20px rgba(39, 174, 96, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #1e8449, #145a32)',
                          boxShadow: '0 6px 25px rgba(39, 174, 96, 0.5)'
                        }
                      }}
                    >
                      {isLoading ? <CircularProgress size={20} color="inherit" /> : t('profile.saveChanges')}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                      disabled={isLoading}
                      sx={{ 
                        minWidth: { xs: '100%', sm: 140 },
                        height: { xs: 40, sm: 44, md: 48 },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        fontWeight: 600,
                        color: 'white',
                        borderColor: 'rgba(255,255,255,0.5)',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)'
                        }
                      }}
                    >
                      {t('common.cancel')}
                    </Button>
                  </Stack>
                )}
              </Box>
            </Box>
            
            {/* Profile Status Indicator */}
            <Box sx={{ mt: { xs: 2, sm: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  opacity: 0.9,
                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' }
                }}>
                  {t('profile.accountStatus')}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  background: 'rgba(39, 174, 96, 0.2)',
                  border: '1px solid rgba(39, 174, 96, 0.3)'
                }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: '#27ae60',
                    animation: 'pulse 2s infinite'
                  }} />
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600, 
                    color: '#27ae60',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    {t('profile.active')}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ 
                width: '100%', 
                height: { xs: 8, sm: 10 }, 
                bgcolor: 'rgba(255,255,255,0.1)', 
                borderRadius: { xs: 4, sm: 5 },
                overflow: 'hidden',
                position: 'relative',
                border: '1px solid rgba(255,255,255,0.1)'
              }}>
                <Box sx={{ 
                  width: '100%', 
                  height: '100%', 
                  background: 'linear-gradient(90deg, #27ae60, #2ecc71, #27ae60)',
                  backgroundSize: '200% 100%',
                  borderRadius: { xs: 4, sm: 5 },
                  animation: 'shimmer 3s ease-in-out infinite'
                }} />
              </Box>
              <Typography variant="body2" sx={{ 
                mt: 1.5, 
                opacity: 0.8, 
                fontStyle: 'italic',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                {t('profile.accountActiveMessage')}
              </Typography>
            </Box>
          </Paper>

          {/* Tabs Section */}
          <Paper 
            elevation={0}
            sx={{ 
              mb: { xs: 2, sm: 3, md: 4 }, 
              borderRadius: { xs: 2, sm: 3 },
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ 
              background: 'linear-gradient(90deg, #f8f9fa 0%, #ffffff 100%)',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange}
                sx={{
                  px: { xs: 1, sm: 2, md: 3 },
                  '& .MuiTab-root': {
                    minHeight: { xs: 48, sm: 56, md: 64 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 700
                    }
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0'
                  }
                }}
              >
                <Tab 
                  label={t('profile.personalInformation')} 
                  icon={<PersonIcon />} 
                  iconPosition="start" 
                />
                                 <Tab 
                   label={t('profile.accountSettings')} 
                   icon={<VpnKeyIcon />} 
                   iconPosition="start" 
                 />
                
              </Tabs>
            </Box>

            {/* Personal Information Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography variant="h5" sx={{ 
                  mb: { xs: 2, sm: 3, md: 4 }, 
                  fontWeight: 600, 
                  color: 'text.primary',
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }}>
                  {t('profile.personalInformation')}
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('profile.firstName')}
                      value={profileData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      disabled={!isEditing}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('profile.lastName')}
                      value={profileData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      disabled={!isEditing}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('profile.emailAddress')}
                      type="email"
                      value={profileData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      disabled={!isEditing}
                      error={!!errors.email}
                      helperText={errors.email}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main'
                          }
                        }
                      }}
                    />
                  </Grid>
                  
                  {/* Social Media Section */}
                  <Grid item xs={12}>

                  </Grid>

                </Grid>
              </Box>
            </TabPanel>

            {/* Account Settings Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography variant="h5" sx={{ 
                  mb: { xs: 2, sm: 3, md: 4 }, 
                  fontWeight: 600, 
                  color: 'text.primary',
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                }}>
                  {t('profile.accountSettings')}
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        height: '100%',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            bgcolor: 'primary.main',
                            color: 'white'
                          }}>
                            <VpnKeyIcon />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t('profile.passwordSecurity')}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                          {t('profile.passwordSecurityDesc')}
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={() => setChangePasswordDialog(true)}
                          startIcon={<VpnKeyIcon />}
                          sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600
                          }}
                        >
                          {t('profile.changePassword')}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 3
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <InfoIcon color="primary" />
                          {t('profile.accountInformation')}
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Box display="flex" alignItems="center" gap={2} p={2}>
                              <InfoIcon color="info" />
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                  {t('profile.accountCreated')}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {profileData.createdAt ? new Date(profileData.createdAt).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'short', 
                                    day: 'numeric' 
                                  }) : 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box display="flex" alignItems="center" gap={2} p={2}>
                              <ScheduleIcon color="success" />
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                  {t('profile.lastLogin')}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {profileData.lastLogin ? new Date(profileData.lastLogin).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'short', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  }) : 'Never'}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>




          </Paper>

          {/* Change Password Dialog */}
          <Dialog 
            open={changePasswordDialog} 
            onClose={() => setChangePasswordDialog(false)}
            maxWidth="xs"
            PaperProps={{
              sx: {
                borderRadius: { xs: 2, sm: 3, md: 4 },
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                mx: { xs: 1, sm: 2, md: 0 },
                width: { xs: '95%', sm: '400px', md: '450px' },
                maxWidth: { xs: '95%', sm: '400px', md: '450px' }
              }
            }}
          >
            <DialogTitle sx={{ 
              pb: 1,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
              color: 'white'
            }}>
              <Box display="flex" alignItems="center" gap={2}>
                <VpnKeyIcon />
                {t('profile.changePassword')}
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, px: { xs: 2, sm: 3 }, pb: 1 }}>
              <Stack spacing={2.5} sx={{ pt: 2 }}>
                <TextField
                  fullWidth
                  label={t('profile.currentPassword')}
                  type={showPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label={t('profile.newPassword')}
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label={t('profile.confirmNewPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 1 }}>
              <Button 
                onClick={() => setChangePasswordDialog(false)}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handlePasswordChange} 
                variant="contained"
                disabled={isLoading}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #3498db, #2980b9)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #2980b9, #1f5f8b)'
                  }
                }}
              >
                {isLoading ? <CircularProgress size={20} color="inherit" /> : t('profile.changePassword')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            sx={{
              '& .MuiSnackbar-root': {
                left: { xs: '50%', sm: 'auto' },
                right: { xs: 'auto', sm: 24 },
                transform: { xs: 'translateX(-50%)', sm: 'none' }
              }
            }}
          >
            <Alert 
              onClose={handleCloseSnackbar} 
              severity={snackbar.severity}
              sx={{ 
                width: '100%',
                borderRadius: 2,
                fontWeight: 600
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Fade>
    </Box>
  );
});

export default Profile;
